import React from 'react';
import { Box, Text, VStack, Skeleton, SkeletonText } from '@chakra-ui/react';
import { useTransactionTypes } from '../hooks/useTransactions';
import { usePlayerTransactions, getTransactionRowsFromPayload } from '../hooks/usePlayerTransactions';
import { formatPstDateTime } from '../lib/pstTime';

interface RecentDominusActivityProps {
  /** dom_rosters.id — GET /transactions/by-player/:leagueId/:rosterPlayerId */
  rosterPlayerId: number;
  leagueId?: number;
  /** Optional viewer team for can_see (backend defaults to auth user team). */
  myTeam?: number;
  maxItems?: number;
  /** When false, skip the request (e.g. card not flipped). */
  enabled?: boolean;
  /** Light PlayerCard back: no duplicate title, cream-friendly colors (skip parent sx hacks). */
  embedded?: boolean;
}

const RecentDominusActivity: React.FC<RecentDominusActivityProps> = ({
  rosterPlayerId,
  leagueId,
  myTeam,
  maxItems = 8,
  enabled = true,
  embedded = false,
}) => {
  const { data: transactionsPayload, isLoading, error } = usePlayerTransactions({
    leagueId,
    rosterPlayerId,
    myTeam,
    page: 0,
    pageSize: maxItems,
    enabled: enabled && !!leagueId,
  });

  // Fetch transaction types for display names
  const { transactionTypes } = useTransactionTypes();

  const transactions = getTransactionRowsFromPayload(transactionsPayload);

  // Helper to get display name from transaction type
  const getTypeDisplayName = (type?: string) => {
    if (!type || !Array.isArray(transactionTypes)) return type || 'Transaction';
    const typeObj = transactionTypes.find(t => t.type === type);
    return typeObj?.displayName || type;
  };

  // Format date using existing PST formatter
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    try {
      return formatPstDateTime(dateStr, true);
    } catch {
      return '';
    }
  };

  const strField = (v: unknown) =>
    typeof v === 'string' ? v : v != null && v !== '' ? String(v) : '';

  // Get display text for transaction
  const getTransactionText = (transaction: Record<string, unknown>) => {
    return (
      strField(transaction.headline) ||
      strField(transaction.type_display) ||
      getTypeDisplayName(
        typeof transaction.type === 'string' ? transaction.type : undefined
      ) ||
      'Transaction'
    );
  };

  // Get date string from transaction
  const getTransactionDate = (transaction: Record<string, unknown>) => {
    const raw = transaction.created_at ?? transaction.createdAt;
    return typeof raw === 'string' ? raw : raw != null ? String(raw) : undefined;
  };

  const titleBlock = !embedded ? (
    <Text fontSize="xl" fontWeight="semibold" color="gray.300" mb={4}>
      Recent Dominus Activity
    </Text>
  ) : null;

  const mutedColor = embedded ? "#8b7355" : "gray.400";
  const lineColor = embedded ? "#d4cbb0" : "gray.600";
  const bodyColor = embedded ? "#333" : "white";
  const skeletonBg = embedded ? "#d4cbb0" : undefined;

  // Loading state
  if (isLoading) {
    return (
      <Box width="100%" pt={embedded ? 0 : 2}>
        {titleBlock}
        <VStack align="flex-start" spacing={2}>
          {[1, 2, 3].map((i) => (
            <Box key={i} width="100%" pb={2}>
              <Skeleton height="16px" width="80%" mb={1} startColor={skeletonBg} endColor={skeletonBg} />
              <SkeletonText
                noOfLines={1}
                spacing="1"
                height="12px"
                width="60%"
                startColor={skeletonBg}
                endColor={skeletonBg}
              />
            </Box>
          ))}
        </VStack>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box width="100%" pt={embedded ? 0 : 2}>
        {titleBlock}
        <Text fontSize="sm" color={mutedColor}>
          Unable to load activity
        </Text>
      </Box>
    );
  }

  // Empty state
  if (transactions.length === 0) {
    return (
      <Box width="100%" pt={embedded ? 0 : 2}>
        {titleBlock}
        <Text fontSize="sm" color={mutedColor}>
          No recent activity
        </Text>
      </Box>
    );
  }

  // Render transactions
  return (
    <Box width="100%" pt={embedded ? 0 : 2}>
      {titleBlock}
      <VStack align="flex-start" spacing={embedded ? 1 : 2}>
        {transactions.map((transaction, idx: number) => {
          const transactionText = getTransactionText(transaction);
          const transactionDate = getTransactionDate(transaction);
          const rowKey =
            transaction.id ??
            transaction.transaction_id ??
            `${transactionDate ?? ''}-${idx}`;

          return (
            <Box
              key={String(rowKey)}
              width="100%"
              pb={2}
              borderBottom="1px solid"
              borderColor={lineColor}
              _last={{ borderBottom: "none", pb: embedded ? 0 : 2 }}
            >
              <Text
                fontSize="sm"
                color={bodyColor}
                fontWeight="medium"
                textAlign="left"
                lineHeight="1.35"
              >
                {transactionText}
              </Text>
              {transactionDate && (
                <Text
                  fontSize="xs"
                  color={mutedColor}
                  textAlign="left"
                  mt={1}
                >
                  {formatDate(transactionDate)}
                </Text>
              )}
            </Box>
          );
        })}
      </VStack>
    </Box>
  );
};

export default RecentDominusActivity;

